// src/utils/coaPanelStatus.js
// Shared panel detection utility — imported by Dashboard, COA, COAOptions, LabOrders

export const TERPENE_KEYS = [
    "beta_pinene", "alpha_pinene", "Terpinolene", "alpha_humulene", "beta_caryophyllene",
    "Myrcene", "Linalool", "Limonene", "Bisabolol", "Camphene", "caryophyllene_oxide",
    "Nerol", "Ocimene", "Geraniol", "terpineol", "alpha_terpinene", "gamma_terpinene",
    "fenchone", "Eucalyptol", "Borneol", "Phytol", "Sabinene", "delta_3_carene", "Elemene",
    "Fenchol", "Guaiol", "Isoborneol", "Isopulegol", "Menthol", "Pulegone", "Valencene",
    "alpha_bisabolene", "beta_bisabolene", "Farnesene", "Cedrene", "Bergamotene", "Aristolene",
    "Selina_diene", "trans_sesquisabinene_hydrate", "gamma_elemene", "beta_maaliene",
    "Bulnesol", "Chamigrene", "Nerolidol",
];

export const CANNABINOID_KEYS = [
    "CBDA", "CBGa", "CBCa", "THCa", "THCVa", "CBD", "delta_9_THC", "D8_THC", "THCV",
    "CBC", "CBG", "CBN", "CBDV", "CBL", "CBE", "CBT", "CBND", "CBDVA", "CBGVA", "CBNA",
    "CBV", "THCP", "CBDP", "CBDH", "THCH", "Delta_10_THC",
];

// A COA's delta-9 THC counts as compliant if either:
//  - it was measured and is <= 0.3%, or
//  - it's NULL but another cannabinoid field is present — the extraction
//    pipeline stores a lab-reported non-detect ("ND"/"<LOQ") as NULL rather
//    than 0, so a genuine non-detect must not be treated as "not tested".
// NULL with no other cannabinoid data means the panel was never run at all.
//
// Exported as the standalone eligibility check for the public compliance page
// and badge downloads — those only care about the THC limit, not full-panel
// completeness or contaminant results (contaminants intentionally ignored for
// now, see CLIENT.md/API.md).
export function isThcCompliant(coa) {
    if (coa.delta_9_THC != null) {
        const v = parseFloat(coa.delta_9_THC);
        return !isNaN(v) && v <= 0.3;
    }
    return coa.THCa != null || coa.CBD != null || coa.CBG != null;
}

// For safety panels, `met` requires the value to be "pass" (case-insensitive).
// "fail", "not tested", or null all count as NOT met — same as missing data.
// `requiredForComplete` — false means the panel is still displayed but does not
// block "full panel" / COA-complete status.
export function getPanelStatus(coa) {
    const isPass = (v) => typeof v === "string" && v.toLowerCase().includes("pass");
    return {
        cannabinoids:   { met: CANNABINOID_KEYS.some((k) => coa[k] != null) && isThcCompliant(coa), isSafetyPanel: false, requiredForComplete: true  },
        terpenes:       { met: TERPENE_KEYS.some((k) => coa[k] != null),     isSafetyPanel: false, requiredForComplete: false },
        heavy_metals:   { met: isPass(coa.heavy_metals),   isSafetyPanel: true,  requiredForComplete: true  },
        pesticides:     { met: isPass(coa.pesticides),     isSafetyPanel: true,  requiredForComplete: true  },
        microbials:     { met: isPass(coa.microbials),     isSafetyPanel: true,  requiredForComplete: true  },
        mycotoxins:     { met: isPass(coa.mycotoxins),     isSafetyPanel: true,  requiredForComplete: true  },
        foreign_matter: { met: isPass(coa.foreign_matter), isSafetyPanel: true,  requiredForComplete: false },
        moisture:       { met: coa.moisture_content != null, isSafetyPanel: false, requiredForComplete: false },
    };
}

// True only if every required panel is met (safety panels require "pass").
// Synthetic-colored products can never be complete, regardless of test results.
export function isCOAComplete(coa) {
    if ((coa.color || "").toLowerCase() === "synthetic") return false;
    return Object.values(getPanelStatus(coa))
        .filter((p) => p.requiredForComplete)
        .every((p) => p.met);
}

// isWholesaleEligible is the same as isCOAComplete since safety panels
// already require "pass" in their `met` check
export const isWholesaleEligible = isCOAComplete;

export const PANEL_LABELS = {
    cannabinoids:   "Cannabinoids",
    terpenes:       "Terpenes",
    heavy_metals:   "Heavy Metals",
    pesticides:     "Pesticides",
    microbials:     "Microbials",
    mycotoxins:     "Mycotoxins",
    moisture:       "Moisture",
    foreign_matter: "Foreign Matter",
};
